import { RefObject, h, Fragment } from "preact";
import { useRef } from "preact/hooks";

import UserAvatar from "../../../apps/containers/UserAvatar";
import RawColor from "../../../foundation/api/model/colors/RawColor";
import User from "../../../foundation/api/model/users/User";
import { TagGroupTypes, Keyboard } from "../../../foundation/enums";
import classname from "../../../foundation/utils/classname";
import { matchUserWithFilterValue } from "../../../foundation/utils/search";
import { getUserDisplayName } from "../../../foundation/utils/user";
import RichInput from "../Input/RichInput";
import Menu from "../Menu";
import TagTypeIndicator from "../TagTypeIndicator";

import styles from "./EditableTag.module.css";

const MEMBER_MENU_VERTICAL_OFFSET = 8;
interface Props {
    name?: string;
    color?: RawColor;
    users?: User[];
    placeholder?: string;
    tagGroupType: TagGroupTypes;
    onCancel?: () => void;
    onSetName: (newName: string) => void;
    onBlur?: (name: string) => void;
    onSelectUser?: (userId: string) => void;
}

export default function EditableTag({
    name = "",
    color,
    users,
    tagGroupType,
    placeholder,
    onBlur,
    onCancel,
    onSelectUser,
    onSetName
}: Props) {
    const baseRef = useRef<HTMLDivElement>();
    const tagNameInputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const filteredUsers = users?.filter(user => matchUserWithFilterValue(user, name));

    function handleTagNameChange(event: Event) {
        onSetName((event.target as HTMLInputElement).value);
    }

    function handleTagNameBlur(event: Event) {
        if (tagGroupType !== TagGroupTypes.member) {
            onBlur?.((event.target as HTMLInputElement).value);
        }
    }

    function handleKeyDown(ev: KeyboardEvent) {
        if (ev.key === Keyboard.Key.Escape) {
            ev.preventDefault();
            onCancel?.();
        } else if (ev.key === Keyboard.Key.Enter) {
            ev.preventDefault();
            tagNameInputRef.current?.blur();
        }
    }

    function handleCloseMemberDropdown() {
        onCancel?.();
    }

    function handleSelectUser(userId: string) {
        return () => onSelectUser?.(userId);
    }

    function handleMembersMenuPosition(menu: HTMLElement) {
        if (baseRef.current) {
            const { x, y, height } = baseRef.current.getBoundingClientRect();
            const menuX = x;
            const menuY = y + height + MEMBER_MENU_VERTICAL_OFFSET;

            menu.style.transform = `translate(${menuX}px, ${menuY}px)`;
        }
    }

    function renderUserAvatar(user: User) {
        // TODO-electron-tech-debt-ozge: Separate displaying user menu logic from EditableTag to not to use container component inside a library component
        return () => (
            <UserAvatar user={user}/>
        );
    }

    return (
        <>
            <div
                ref={baseRef}
                class={classname(styles.editableTag, "noSelect", {
                    [styles.hasTypeIndicator]: tagGroupType !== TagGroupTypes.default
                })}>
                <TagTypeIndicator tagGroupType={tagGroupType} color={color}/>
                <RichInput
                    class={classname(styles.tagName, "body-1")}
                    inputClass={classname(styles.tagNameInput, "ellipsis")}
                    placeholder={placeholder || "Tag"}
                    value={name}
                    inputRef={tagNameInputRef}
                    autoFocus
                    onInput={handleTagNameChange}
                    onBlur={handleTagNameBlur}
                    onKeyDown={handleKeyDown}/>
            </div>
            {
                tagGroupType === TagGroupTypes.member && users && onSelectUser && (
                    <Menu.Overlay
                        class={styles.membersMenu}
                        onPositionMenu={handleMembersMenuPosition}
                        onClose={handleCloseMemberDropdown}>
                        {
                            filteredUsers?.map(user => (
                                <Menu.Button
                                    key={user._id}
                                    icon={renderUserAvatar(user)}
                                    content={getUserDisplayName(user)}
                                    onClick={handleSelectUser(user._id)}/>
                            ))
                        }
                        {
                            !filteredUsers || filteredUsers.length === 0 && (
                                <div class={classname("caption-1", styles.noMatchingUser)}>
                                    No member with this name 🤷‍♀️
                                </div>
                            )
                        }
                    </Menu.Overlay>
                )
            }
        </>
    );
}
